/**
 * RoleGuard — role-based access control for protected routes.
 *
 * Checks the authenticated user's roles against an allowlist.
 * Redirects unauthorized users to a fallback route.
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth';
import { ROUTES } from '@/app/routes/paths';
import type { GuardProps } from './types';
import type { Role } from '@/types/auth';

export interface RoleGuardProps extends GuardProps {
  roles: Role[];
  redirectTo?: string;
}

export function RoleGuard({
  children,
  roles,
  redirectTo = ROUTES.HOME,
}: RoleGuardProps): JSX.Element {
  const { user, state } = useAuth();

  if (state !== 'authenticated' || !user) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  const hasRole = user.roles.some((role: Role) => roles.includes(role));
  if (!hasRole) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

RoleGuard.displayName = 'RoleGuard';
